<template>
  <table-index
    :config="tableConfig"
  >
    <template
      #dynamic-content
    >
      <information-dialog
        v-if="informationDialogConfig.show"
        :open-dialog="informationDialogConfig.show"
        :dialog-message="informationDialogConfig.message"
        :positive-button-text="informationDialogConfig.positiveButtonText"
        :negative-button-text="informationDialogConfig.negativeButtonText"
        :confirm-callback-function="informationDialogConfig.confirmCallbackFunction"
        :title="informationDialogConfig.title"
        @close="informationDialogConfig.show = false"
      />
    </template>
  </table-index>
</template>

<script>
import TableIndex from '@/global/components/table/TableIndex.vue'
import InformationDialog from '@/global/components/view-layouts/tabs-layout/components/dialogs/InformationDialog.vue'
import { api } from '@/global/services/api'
import { formatIsoDateCustom } from '@/global/services/helpers/dates'

const FORM_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'
const DISPLAY_DATETIME_FORMAT = 'DD.MM.YYYY. HH:mm'
export default {
  name: 'VehicleReservationHistory',

  components: {
    InformationDialog,
    TableIndex
  },

  data () {
    return {
      informationDialogConfig: {
        show: false,
        message: '',
        positiveButtonText: this.$t('fleet/vehicle_reservation.cancel_reservation'),
        negativeButtonText: this.$t('base.cancel'),
        confirmCallbackFunction: null,
        title: ''
      },
      fetchTableData: false,
      setInitialFilters: true,
      showLoader: false
    }
  },

  computed: {
    tableConfig () {
      return {
        tableConfig: {
          apiConfig: {
            get: {
              module: 'fleet',
              route: 'vehicle-reservations/history',
              method: 'get'
            }
          },
          callFetchData: this.fetchTableData,
          showLoader: this.showLoader,
          onDataReceived: () => {
            this.fetchTableData = false
          },
          onDataReceivedWithParams: async (event) => {
            if (this.setInitialFilters) {
              this.setInitialFilters = false
              this.showLoader = true
              const { updateFilters, fetchData } = event || {}
              const { vehicleId } = this.$route?.query || {}

              if (typeof fetchData === 'function' && typeof updateFilters === 'function') {
                const today = new Date()

                const dateBefore = new Date(today)
                dateBefore.setDate(today.getDate() - 14)

                const dateAfter = new Date(today)
                dateAfter.setDate(today.getDate() + 15)

                const formatDate = (date) =>
                  String(date.getDate()).padStart(2, '0') + '.' +
                  String(date.getMonth() + 1).padStart(2, '0') + '.' +
                  date.getFullYear() + '. ' +
                  String(date.getHours()).padStart(2, '0') + ':' +
                  String(date.getMinutes()).padStart(2, '0')

                const formattedBefore = formatDate(dateBefore)
                const formattedAfter = formatDate(dateAfter)

                updateFilters('vehicle_id', vehicleId ? parseInt(vehicleId) : null)
                updateFilters('date_start', formattedBefore || null)
                updateFilters('date_end', formattedAfter || null)

                await fetchData()
                this.showLoader = false
              }
            }
          },
          tableConfig: {
            styles: {
              useFixedHeaderWidth: true,
              headersHeight: '76px',
              dataRowHeight: '64px'
            },
            cellsConfig: {
              status: {
                custom_display_value: (data) => {
                  return data?.status ? this.$t(data.status) : '-'
                }
              },
              date_start: {
                custom_display_value: (data) => {
                  return data?.date_start ? formatIsoDateCustom(data.date_start, FORM_DATETIME_FORMAT, DISPLAY_DATETIME_FORMAT) : '-'
                }
              },
              date_end: {
                custom_display_value: (data) => {
                  return data?.date_end ? formatIsoDateCustom(data.date_end, FORM_DATETIME_FORMAT, DISPLAY_DATETIME_FORMAT) : '-'
                }
              },
              created_by: {
                custom_display_value: (data) => {
                  return data?.created_by ? data.created_by : '-'
                }
              },
              deactivated_by: {
                custom_display_value: (data) => {
                  return data?.deactivated_by ? data.deactivated_by : '-'
                }
              }
            },
            headers: [
              {
                label: this.$t('fleet/vehicle_reservation.datetime_of_reservation'),
                key: 'created_at',
                style: {
                  width: '200px'
                },
                always_visible: true
              },
              {
                label: this.$t('fleet/vehicle_reservation.reserved_by'),
                key: 'driver_full_name',
                style: {
                  width: '130px'
                },
                always_visible: true
              },
              {
                label: this.$t('fleet/vehicle.registration'),
                key: 'registration',
                style: {
                  width: '130px'
                },
                always_visible: true
              },
              {
                label: this.$t('fleet/vehicle.label'),
                key: 'label',
                style: {
                  width: '130px'
                },
                always_visible: true
              },
              {
                label: this.$t('fleet/vehicle_reservation.date_from'),
                key: 'date_start',
                style: {
                  width: '200px'
                },
                always_visible: true
              },
              {
                label: this.$t('fleet/vehicle_reservation.date_to'),
                key: 'date_end',
                style: {
                  width: '200px'
                },
                always_visible: true
              },
              {
                label: this.$t('fleet/vehicle.status'),
                key: 'status',
                style: {
                  width: '130px'
                },
                always_visible: true
              },
              {
                label: this.$t('fleet/vehicle_reservation.created_by'),
                key: 'created_by',
                style: {
                  width: '160px'
                },
                always_visible: true
              },
              {
                label: this.$t('fleet/vehicle_reservation.canceled_by'),
                key: 'deactivated_by',
                style: {
                  width: '160px'
                },
                always_visible: true
              }
            ],
            showActions: (event) => {
              const { index, data } = event || {}

              if (data && data[index]) {
                const { isActive } = data[index] || {}
                return !!isActive
              }

              return false
            },
            actionsConfig: [
              {
                label: 'Otkaži rezervaciju',
                icon: 'mdi-book-cancel-outline',
                click: (event) => this.onDeactivateReservation(event)
              }
            ],
            filtersConfig: {
              fields: [
                {
                  key: 'vehicle_id',
                  label: 'Odaberite vozilo',
                  type: 'autocomplete',
                  autofocus: false,
                  pickedId: this.$route.query.vehicleId,
                  autocomplete_options: {
                    module: 'fleet',
                    fetchInitial: false,
                    route: 'vehicles/autocomplete'
                  }
                },
                {
                  key: 'driver_id',
                  label: 'Odaberite vozača',
                  type: 'autocomplete',
                  autocomplete_options: {
                    module: 'people',
                    route: 'people/autocomplete'
                  }
                },
                {
                  key: 'date_start',
                  label: 'Datum početka',
                  type: 'datetime-picker',
                  appendIcon: 'mdi-calendar-month',
                  format: 'DD.MM.YYYY. HH:mm',
                  placeholder: 'DD.MM.YYYY. HH:mm'
                },
                {
                  key: 'date_end',
                  label: 'Datum završetka',
                  type: 'datetime-picker',
                  appendIcon: 'mdi-calendar-month',
                  format: 'DD.MM.YYYY. HH:mm',
                  placeholder: 'DD.MM.YYYY. HH:mm'
                }
              ]
            },
            paginationConfig: {
              perPageOptions: [20, 50, 100],
              perPage: 20,
              currentPage: 1
            }
          }
        },
        breadcrumbs: {
          items: [
            {
              text: this.$t('fleet.vehicles_reservation')
            },
            {
              text: this.$t('fleet/vehicle_reservation.reservations_history'),
              bolded: true
            }
          ]
        }
      }
    }
  },

  methods: {
    onDeactivateReservation (event) {
      const { registration, id: reservationId } = event?.data || {}

      const registrationValue = registration || '-'
      this.$set(this.informationDialogConfig, 'show', true)
      this.$set(this.informationDialogConfig, 'message', this.$t('fleet/vehicle_reservation.cancel_vehicle_reservation_confirmation').replace(':vehicle', registrationValue))
      this.$set(this.informationDialogConfig, 'title', this.$t('fleet/vehicle_reservation.cancel_reservation_for_vehicle').replace(':vehicle', registrationValue))
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', async () => await this.cancelReservation(reservationId))
    },

    async cancelReservation (reservationId) {
      try {
        if (!reservationId) return

        await api('fleet').post(`vehicle-reservations/${reservationId}/deactivate`)
        this.$set(this.informationDialogConfig, 'show', false)
        this.fetchTableData = true
      }
      catch (exception) {
        console.error(exception)
      }
    }
  }
}
</script>

<style scoped>
</style>
